// Where the guest lands after the payment window — TICKETING-SPEC.md §5.3.
//
// Before this existed the payment window returned people to a bare event list,
// with nothing to say whether their money had gone anywhere. For a ticket that
// is the entire question.
//
// It has to handle three outcomes, not two:
//
//   ?order=<id>    paid. The tickets may not exist YET — they are issued by the
//                  webhook, which can land after the guest is already back — so
//                  the page waits, rather than telling someone who just paid
//                  that nothing happened.
//   ?afbrudt=<id>  the guest backed out of the payment window. Nothing was
//                  charged and the seats go back on their own.
//   neither        somebody opened the URL by hand.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { renderInfoText } from '../../utils/ticketInfoText';
import configData from '../../config.json';
import './styles.css';

const API = (configData && configData.SERVER_URL) || 'http://localhost:5001/api';

// Restaurant.Currency is a currencies.json entry ({ code, symbol_native, … })
// on most venues and a plain string on a few; rendered raw, the object printed
// as "150,00 [object Object]".
const currencyLabel = (currency) =>
  (currency && typeof currency === 'object' ? currency.symbol_native || currency.code : currency) || 'kr';

const money = (n, currency) =>
  `${(Number(n) || 0).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currencyLabel(currency)}`;

/*
 * The order carries the wall-clock date as the venue typed it ('2026-10-03').
 * Printing that raw left the confirmation saying "2026-10-03 kl. 20:00" while
 * the ticket page beside it said "lørdag 3. oktober" — the same event, two
 * voices. Same formatting as pages/Ticket.
 */
const formatWhen = (event) => {
  if (!event || !event.Date) return '';
  const d = new Date(`${event.Date}T00:00:00`);
  if (isNaN(d.getTime())) return [event.Date, event.Start].filter(Boolean).join(' kl. ');
  const day = d.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' });
  return event.Start ? `${day} kl. ${event.Start}` : day;
};

/*
 * How long to keep asking before saying something honest instead.
 *
 * The webhook is normally a second or two behind the guest. Ten seconds of
 * polling covers that with room to spare; past ~25s something is genuinely
 * slow, and at that point the truthful thing is "we have your payment, the
 * ticket is on its way by SMS" — not a spinner that never resolves, and
 * certainly not an error, because the money HAS been taken.
 */
const POLL_MS = 1500;
const GIVE_UP_MS = 25000;

export default function Confirmation() {
  const { restaurantId } = useParams();
  const [params] = useSearchParams();

  const orderId = params.get('order');
  const cancelledId = params.get('afbrudt');
  // The order's view token: the order id alone is guessable, and this page
  // shows ticket codes.
  const token = params.get('t') || '';

  const [data, setData] = useState(null);
  const [slow, setSlow] = useState(false);
  const [failed, setFailed] = useState(false);
  const startedAt = useRef(Date.now());
  const timer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/order/${orderId}?t=${encodeURIComponent(token)}`, { validateStatus: () => true });
      if (res.status !== 200) {
        setFailed(true);
        return;
      }
      setData(res.data);

      // Final answers: stop polling.
      if (['ready', 'cancelled', 'failed', 'stale'].indexOf(res.data.state) >= 0) return;

      if (Date.now() - startedAt.current > GIVE_UP_MS) {
        setSlow(true);
        return;
      }
      timer.current = setTimeout(poll, POLL_MS);
    } catch (err) {
      // A dropped request is not a failed payment. Keep trying until the
      // give-up window closes.
      if (Date.now() - startedAt.current > GIVE_UP_MS) setSlow(true);
      else timer.current = setTimeout(poll, POLL_MS);
    }
  }, [orderId, token]);

  useEffect(() => {
    if (!orderId) return undefined;
    poll();
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [orderId, poll]);

  const backToEvents = (
    <Link className="ev-buy ev-buy-link" to={`/events/${restaurantId}`}>
      Tilbage til arrangementer
    </Link>
  );

  /* ------------------------------------------------------------- cancelled */

  if (cancelledId) {
    return (
      <div className="ev-page">
        <div className="ev-card">
          <div className="ev-body ev-center">
            <div className="ev-emoji">🙂</div>
            <h1 className="ev-title">Betalingen blev afbrudt</h1>
            <p className="ev-desc">
              Der er ikke trukket noget, og billetterne er lagt tilbage. Du kan prøve igen
              når du er klar.
            </p>
            {backToEvents}
          </div>
        </div>
      </div>
    );
  }

  if (!orderId) {
    return (
      <div className="ev-page">
        <div className="ev-card">
          <div className="ev-body ev-center">
            <p className="ev-desc">Der er ingen ordre at vise.</p>
            {backToEvents}
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- failed */

  if (failed) {
    return (
      <div className="ev-page">
        <div className="ev-card">
          <div className="ev-body ev-center">
            <div className="ev-emoji">🤔</div>
            <h1 className="ev-title">Vi kan ikke finde ordren</h1>
            <p className="ev-desc">
              Tjek linket i din kvitterings-SMS. Er der trukket penge, er billetten
              udstedt — kontakt stedet hvis du er i tvivl.
            </p>
            {backToEvents}
          </div>
        </div>
      </div>
    );
  }

  /* --------------------------------------------------------------- waiting */

  /*
   * The server's final "no" answers, each in plain words. Before, every one of
   * them fell through to "Dine billetter er klar" with an empty list, or to
   * "Din betaling er modtaget" after 25 seconds — neither true.
   */
  const finalState = data && {
    failed: {
      emoji: '😕',
      title: 'Billetterne blev udsolgt',
      text: 'Din betaling gik igennem, men der var ikke flere pladser, da den kom frem. Du får pengene tilbage — kontakt stedet, hvis du har spørgsmål.',
    },
    cancelled: {
      emoji: '↩️',
      title: 'Ordren er annulleret',
      text: 'Billetterne på denne ordre er annulleret eller refunderet og kan ikke bruges.',
    },
    stale: {
      emoji: '🙂',
      title: 'Betalingen gik ikke igennem',
      text: 'Der er ikke trukket noget, og pladserne er givet fri igen. Du kan prøve igen.',
    },
  }[data.state];

  if (finalState) {
    return (
      <div className="ev-page">
        <div className="ev-card">
          <div className="ev-body ev-center">
            <div className="ev-emoji">{finalState.emoji}</div>
            <h1 className="ev-title">{finalState.title}</h1>
            <p className="ev-desc">{finalState.text}</p>
            {backToEvents}
          </div>
        </div>
      </div>
    );
  }

  if (!slow && (!data || data.state === 'pending' || data.state === 'processing')) {
    const free = data && Number(data.total) === 0;
    return (
      <div className="ev-page">
        <div className="ev-card">
          <div className="ev-body ev-center">
            <div className="ev-spinner" />
            <h1 className="ev-title">{free ? 'Bekræfter din tilmelding…' : 'Bekræfter din betaling…'}</h1>
            <p className="ev-desc">Det tager normalt et øjeblik. Luk ikke siden.</p>
          </div>
        </div>
      </div>
    );
  }

  /*
   * Paid, but the tickets still are not here. Say what is true: the money is in,
   * the ticket is coming. An error here would be a lie, and a spinner forever
   * would be worse.
   */
  if (slow && (!data || data.state !== 'ready')) {
    return (
      <div className="ev-page">
        <div className="ev-card">
          <div className="ev-body ev-center">
            <div className="ev-emoji">📩</div>
            <h1 className="ev-title">
              {data && data.state === 'processing' ? 'Din betaling er modtaget' : 'Vi venter stadig på betalingen'}
            </h1>
            <p className="ev-desc">
              {data && data.state === 'processing'
                ? 'Billetterne er på vej til dig på SMS og e-mail. Det kan tage et par minutter — du behøver ikke gøre mere.'
                : 'Det tager længere end normalt. Har du gennemført betalingen, kommer billetterne på SMS og e-mail. Ellers kan du prøve igen.'}
            </p>
            {backToEvents}
          </div>
        </div>
      </div>
    );
  }

  /* ----------------------------------------------------------------- ready */

  const event = data.event || {};
  const when = formatWhen(event);

  return (
    <div className="ev-page">
      <div className="ev-card">
        <div className="ev-body">
          <div className="ev-center">
            <div className="ev-emoji">🎫</div>
            <h1 className="ev-title">
              {data.tickets.length === 1 ? 'Din billet er klar' : 'Dine billetter er klar'}
            </h1>
            <p className="ev-desc">
              {event.Name}{when ? ` · ${when}` : ''}
            </p>
          </div>

          {/* The codes, right here. The guest has just paid and is standing
              somewhere with a phone — making them find an SMS first would be
              one step too many. */}
          <div className="ev-tickets">
            {data.tickets.map((t) => (
              t.status === 'cancelled' || t.status === 'refunded' ? (
                <div className="ev-ticket ev-ticket-dead" key={t.code}>
                  <div>
                    <div className="ev-ticket-type">{t.type}</div>
                    <div className="ev-ticket-hint">{t.status === 'refunded' ? 'Refunderet' : 'Annulleret'}</div>
                  </div>
                </div>
              ) : (
                <Link className="ev-ticket" key={t.code} to={`/t/${t.code}`}>
                  <div>
                    <div className="ev-ticket-type">{t.type}</div>
                    <div className="ev-ticket-hint">Vis QR-kode</div>
                  </div>
                  <span className="ev-ticket-code">{t.code}</span>
                </Link>
              )
            ))}
          </div>

          {Number(data.total) > 0 ? (
            <div className="ev-sum">
              <div className="ev-sum-row">
                <span>Billetter</span>
                <span>{money(data.ticketsTotal, data.currency)}</span>
              </div>
              {Number(data.feeTotal) > 0 ? (
                <div className="ev-sum-row ev-sum-fee">
                  <span>Billetgebyr</span>
                  <span>{money(data.feeTotal, data.currency)}</span>
                </div>
              ) : null}
              <div className="ev-sum-row ev-sum-total">
                <span>Betalt</span>
                <span>{money(data.total, data.currency)}</span>
              </div>
            </div>
          ) : null}

          {event.infoText ? (
            <div className="ev-info">{renderInfoText(event.infoText)}</div>
          ) : null}

          <p className="ev-desc ev-center ev-small">
            Du får dem også på SMS og e-mail. Vis QR-koden ved indgangen.
          </p>

          {/* A guest who just got their tickets is the likeliest to want more —
              for a friend, or for the next event. */}
          <Link className="ev-buy ev-buy-link" to={`/events/${restaurantId}`}>
            Køb flere billetter
          </Link>
        </div>
      </div>
    </div>
  );
}
